// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/ManageStaff/StaffFilters.js
import React, { useCallback } from 'react';
import { ArrowUpDown, RotateCcw } from 'lucide-react';

const StaffFilters = ({ filters, onFiltersChange, roles }) => {
  const handleFilterChange = useCallback((key, value) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    onFiltersChange(newFilters);
  }, [filters, onFiltersChange]);

  const handleClearFilters = useCallback(() => {
    const clearedFilters = {
      searchTerm: '',
      role: 'all',
      status: 'all',
      sortBy: 'name',
      sortOrder: 'asc'
    };
    onFiltersChange(clearedFilters);
  }, [onFiltersChange]);

  const handleSortOrderToggle = useCallback(() => {
    const newSortOrder = filters.sortOrder === 'asc' ? 'desc' : 'asc';
    handleFilterChange('sortOrder', newSortOrder);
  }, [filters.sortOrder, handleFilterChange]);

  const getUniqueRoles = useCallback(() => {
    if (!roles || !Array.isArray(roles)) return [];
    
    const uniqueRoles = new Set();
    roles.forEach(role => {
      if (role && typeof role === 'string' && role.trim()) {
        const normalizedRole = role.trim();
        if (normalizedRole.toLowerCase() === 'faculty') {
          uniqueRoles.add('faculty');
        } else {
          uniqueRoles.add(normalizedRole.toLowerCase());
        }
      }
    });
    
    return Array.from(uniqueRoles);
  }, [roles]);

  const uniqueRoles = getUniqueRoles();

  const getDisplayRole = (role) => {
    if (role === 'faculty') return 'Faculty';
    if (role === 'coe') return 'Controller of Examination';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const hasActiveFilters = filters.role !== 'all' || 
                           filters.status !== 'all' || 
                           filters.searchTerm || 
                           filters.sortBy !== 'name' || 
                           filters.sortOrder !== 'asc';

  return (
    <div className="flex flex-col gap-2.5 w-full sm:w-auto">
      {/* Mobile-First Layout: Stacked grids on mobile, flat row on tablet/desktop */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2">
        
        {/* Role Select Dropdown */}
        <select
          value={filters.role || 'all'}
          onChange={(e) => handleFilterChange('role', e.target.value)}
          className="h-9 px-3 text-xs font-medium bg-slate-50 border border-slate-200 text-slate-800 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-all cursor-pointer"
        >
          <option value="all">All Roles</option>
          {uniqueRoles.map((role, index) => (
            <option key={`role-${index}-${role}`} value={role}>
              {getDisplayRole(role)}
            </option>
          ))}
        </select>

        {/* Status Select Dropdown */}
        <select
          value={filters.status || 'all'}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="h-9 px-3 text-xs font-medium bg-slate-50 border border-slate-200 text-slate-800 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-all cursor-pointer"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="disabled">Disabled</option>
        </select>

        {/* Sort Attributes Dropdown */}
        <select
          value={filters.sortBy || 'name'}
          onChange={(e) => handleFilterChange('sortBy', e.target.value)}
          className="h-9 px-3 text-xs font-medium bg-slate-50 border border-slate-200 text-slate-800 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-all cursor-pointer col-span-1"
        >
          <option value="name">Sort by Name</option>
          <option value="role">Sort by Role</option>
          <option value="status">Sort by Status</option>
          <option value="email">Sort by Email</option>
        </select>

        {/* Direction Toggle Trigger Button */}
        <button
          type="button"
          onClick={handleSortOrderToggle}
          className="h-9 px-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-lg transition-colors flex items-center justify-center gap-1.5 select-none col-span-1"
          aria-label={`Sort ${filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
        >
          <ArrowUpDown size={13} className="text-slate-400 shrink-0" />
          <span>{filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}</span>
        </button>
      </div>

      {/* Clear Filters Callout Row */}
      {hasActiveFilters && (
        <div className="flex justify-end sm:justify-start">
          <button
            type="button"
            onClick={handleClearFilters}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 hover:text-rose-700 transition-colors px-1 py-0.5"
          >
            <RotateCcw size={11} />
            <span>Clear Filters</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default StaffFilters;